import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import { useCallback, useEffect, useRef, useState } from 'react';
import PublicTendersTable from '../components/PublicTenders/PublicTendersTable';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { apiService } from '../services/api';
import { getPrivateUserTenders, getPublicTenderStoreDetails } from '../services/tenderCoreService';
import type { Tender } from '../types';

const isLimitError = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes('limit') ||
    lower.includes('billing period') ||
    lower.includes('subscription') ||
    lower.includes('renew')
  );
};

const getDisplayErrorMessage = (
  err: Error & { response?: { status?: number; data?: { message?: string } } }
): string => {
  const rawMessage = err.response?.data?.message || err.message || 'Please try again.';
  const isServerError = err.response?.status && err.response.status >= 500;
  return isServerError ? 'An internal server error occurred. Please try again later.' : rawMessage;
};

// Remove fake data if backend integration is complete for store tenders
// const fakeTenders = [ ... ];

interface Props {
  isWizardMode?: boolean;
  onSuccess?: (tenderId?: string) => void;
}

const PublicTendersPage = ({ isWizardMode, onSuccess }: Props = {}) => {
  const { idToken } = useAuth();
  const { account } = useAccount(); // Get user object for user.uid
  const { showSnackbar } = useSnackbar();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [storeTenders, setStoreTenders] = useState<Tender[]>([]);
  const [isLoadingStoreTenders, setIsLoadingStoreTenders] = useState(true); // Start true
  const [storeTendersError, setStoreTendersError] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [pursuedTendersMap, setPursuedTendersMap] = useState<
    Record<string, { ownerName: string; teamName: string; ownerId: string }>
  >({});

  const fetchStoreTenders = useCallback(async () => {
    if (!account?.uid) {
      // Ensure user and uid are available
      setStoreTendersError('User not authenticated. Cannot fetch public tenders.');
      setIsLoadingStoreTenders(false);
      setStoreTenders([]);
      return;
    }
    setIsLoadingStoreTenders(true);
    setStoreTendersError('');

    try {
      const [publicData, clientTenders, privateData] = await Promise.all([
        getPublicTenderStoreDetails(),
        apiService.getTenders({ includeArchived: true }).catch(() => [] as Tender[]),
        getPrivateUserTenders(account.uid),
      ]);

      const pursuedMap: Record<string, { ownerName: string; teamName: string; ownerId: string }> =
        {};
      for (const t of clientTenders) {
        if (t.sourceStoreTenderId && t.pursuitStatus === 'pursuing') {
          pursuedMap[t.sourceStoreTenderId] = {
            ownerName: t.ownerName || 'Unknown',
            teamName: t.teamName || 'Unknown',
            ownerId: t.ownerId || '',
          };
        }
      }
      setPursuedTendersMap(pursuedMap);

      const publicTenders = publicData.map((data) => {
        const timestamp = data.llmProcessingTimestamp;
        const processedTimestamp =
          timestamp &&
          typeof timestamp === 'object' &&
          'toDate' in timestamp &&
          typeof (timestamp as { toDate: () => Date }).toDate === 'function'
            ? (timestamp as { toDate: () => Date }).toDate()
            : timestamp;

        return {
          ...data,
          llmProcessingTimestamp: processedTimestamp,
        } as unknown as Tender;
      });

      const privateTendersMap = new Map();
      for (const d of privateData) {
        privateTendersMap.set(d.id, d);
      }

      const displayTenders = publicTenders.filter((publicTender) => {
        const pData = privateTendersMap.get(publicTender.id);
        if (pData) {
          // If in private store, only show if not archived AND not active
          const shouldShow = !pData.archived && pData.status !== 'active';
          return shouldShow;
        }
        return true; // If not in private store, it's available by default
      });

      setStoreTenders(displayTenders as Tender[]);
    } catch (err: unknown) {
      const error = err as Error;
      setStoreTendersError(`Failed to load public tenders. ${error.message}`);
      setStoreTenders([]);
    } finally {
      setIsLoadingStoreTenders(false);
    }
  }, [account]); // Dependency on user object

  useEffect(() => {
    // Fetch tenders when component mounts and user is available
    if (account?.uid) {
      // Check for user.uid before fetching
      fetchStoreTenders();
    }
    // Removed fake data loading block
  }, [account, fetchStoreTenders]); // Add fetchStoreTenders to dependencies

  const handleAddTenderToDashboard = useCallback(
    async (tender: Tender) => {
      if (!idToken || !tender?.id || !account) {
        showSnackbar('Authentication error or tender data missing.', 'error');
        return;
      }

      setIsLoadingStoreTenders(true);
      setActionError(null);
      try {
        const result = await apiService.addTenderToDashboard(tender.id);

        if (result?.success) {
          if (isWizardMode && onSuccess) {
            onSuccess(result.data?.id);
          } else {
            fetchStoreTenders(); // This will set loading to false on completion
          }
        } else {
          throw new Error(result?.message || 'Backend failed to add tender.');
        }
      } catch (error: unknown) {
        const err = error as Error & {
          response?: { status?: number; data?: { message?: string } };
        };
        setIsLoadingStoreTenders(false);

        const rawMessage = err.response?.data?.message || err.message || 'Please try again.';
        if (
          rawMessage.includes('SharePoint authentication failed') ||
          rawMessage.includes('SharePoint access denied')
        ) {
          setActionError(rawMessage);
        } else if (!isLimitError(rawMessage)) {
          const displayMsg = getDisplayErrorMessage(err);
          showSnackbar(`Failed to add tender to dashboard. ${displayMsg}`, 'error');
        }

        throw error;
      }
    },
    [idToken, fetchStoreTenders, showSnackbar, account, isWizardMode, onSuccess]
  );

  const handleRemoveStoreTender = useCallback(
    async (tender: Tender, reason: string) => {
      if (!idToken || !tender?.id) {
        showSnackbar('Authentication error or tender data missing for removal.', 'error');
        return;
      }
      setIsLoadingStoreTenders(true);
      try {
        const result = await apiService.archiveStoreTender(tender.id, reason, tender.title);
        if (result?.success) {
          showSnackbar(`${tender.title} has been hidden from this view.`, 'success');
          fetchStoreTenders();
        } else {
          throw new Error(result?.message || 'Backend failed to hide tender.');
        }
      } catch (error: unknown) {
        const err = error as Error;
        showSnackbar(
          `Failed to hide ${tender.title}. ${err.message || 'Please try again.'}`,
          'error'
        );
        setIsLoadingStoreTenders(false);
      }
    },
    [idToken, fetchStoreTenders, showSnackbar]
  );

  let content: React.ReactNode;
  if (isLoadingStoreTenders && storeTenders.length === 0) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading available tenders...</Typography>
      </Box>
    );
  } else if (storeTendersError) {
    content = (
      <Typography color="error" sx={{ my: 2, textAlign: 'center' }}>
        {storeTendersError}
      </Typography>
    );
  } else if (storeTenders.length === 0 && !isLoadingStoreTenders) {
    content = (
      <Typography sx={{ my: 4, textAlign: 'center', color: 'text.primary' }}>
        No tenders currently available in public tenders for you.
      </Typography>
    );
  } else if (storeTenders.length > 0) {
    content = (
      <PublicTendersTable
        tenders={storeTenders}
        onAddTender={handleAddTenderToDashboard}
        onRemoveTender={handleRemoveStoreTender}
        isLoading={isLoadingStoreTenders}
        pursuedTendersMap={pursuedTendersMap}
      />
    );
  } else {
    content = (
      <Typography sx={{ my: 4, textAlign: 'center', color: 'text.primary' }}>
        Please try refreshing the page.
      </Typography>
    );
  }

  const pageContent = (
    <Box>
      <Typography variant="h5" sx={{ fontSize: '1rem', mb: 1.5, mt: -1 }}>
        This page shows publicly available tenders. You can run a capability analysis by adding a
        tender to your Go / No-Go watchlist, simply press the Add button at the bottom of each
        tender card.
      </Typography>
      <Alert
        severity="info"
        sx={{
          mb: 2,
          alignItems: 'center',
          fontSize: '1rem',
          '& .MuiAlert-icon': {
            color: 'bidlogiq.brick',
          },
        }}
      >
        <strong>Note:</strong> Always check the source platform for tender updates before
        submitting.
      </Alert>
      {actionError && (
        <Alert
          severity="error"
          onClose={() => setActionError(null)}
          sx={{
            mb: 2,
            alignItems: 'center',
            fontSize: '1rem',
          }}
        >
          {actionError}
        </Alert>
      )}
      <Box ref={tableContainerRef}>{content}</Box>
    </Box>
  );

  return isWizardMode ? (
    pageContent
  ) : (
    <PageContainer data-testid="public-tenders" title="Explore public tenders" maxWidth={false}>
      {pageContent}
    </PageContainer>
  );
};

export default PublicTendersPage;
